import { useCallback, useState } from "react";

const KEY_PREFIX = "nq.hint.dismissed.";

/**
 * A tip someone only needs once.
 *
 * Explaining a rule is worth doing; explaining it on every visit forever is
 * nagging, and a permanent box in a panel is what pushed the previous version
 * of this into looking like a warning. Dismissal is remembered per browser —
 * losing it on a new machine costs one glance at a sentence they already
 * understood.
 *
 * Reads localStorage lazily and tolerates it being unavailable (Safari private
 * mode, storage disabled), where the tip simply shows every time rather than
 * throwing.
 */
export function useDismissibleHint(id: string): {
  dismissed: boolean;
  dismiss: () => void;
} {
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(KEY_PREFIX + id) === "1";
    } catch {
      return false;
    }
  });

  const dismiss = useCallback(() => {
    setDismissed(true);
    try {
      localStorage.setItem(KEY_PREFIX + id, "1");
    } catch {
      // Not worth surfacing: the tip is closed for this session either way.
    }
  }, [id]);

  return { dismissed, dismiss };
}
